import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { app, auth } from "../firebase";
import CreateUser from "./create-user";
import { sendActivationEmail } from "./staffApi";

/**
 * View staff for a specific organization
 */
export default function ViewStaff({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState("");
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [notice, setNotice] = useState("");
  const [activation, setActivation] = useState({});

  useEffect(() => {
    async function fetchOrganizationAndStaff() {
      try {
        const db = getFirestore(app);
        
        // Fetch organization
        const orgDoc = await getDoc(doc(db, "organizations", id));
        if (!orgDoc.exists()) {
          setError("Organization not found");
          return;
        }
        const orgData = { id: orgDoc.id, ...orgDoc.data() };
        setOrganization(orgData);

        // Fetch staff
        const staffRef = collection(db, "users");
        const q = query(staffRef, where("orgid", "==", id));
        const querySnapshot = await getDocs(q);
        
        const staffData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setStaff(staffData);
      } catch (e) {
        console.error("Error fetching data:", e);
        setError("Failed to load data");
      }
    }

    if (!creatingStaff) {
      fetchOrganizationAndStaff();
    }
  }, [id, creatingStaff]);

  async function handleSendActivation(member) {
    setActivation((current) => ({ ...current, [member.id]: "sending" }));
    try {
      await sendActivationEmail({ auth, email: member.email });
      setActivation((current) => ({ ...current, [member.id]: "sent" }));
    } catch (e) {
      console.error("Error sending activation email:", e);
      setActivation((current) => ({ ...current, [member.id]: "failed" }));
    }
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
        <button
          onClick={() => navigate("/organizations")}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-900"
        >
          ← Back to Organizations
        </button>
      </div>
    );
  }

  if (!organization || !staff) {
    return <p>Loading...</p>;
  }

  // If we are creating a staff member, show the form.
  if (creatingStaff) {
    return <CreateUser organization={organization} setCreatingStaff={setCreatingStaff} onCreated={setNotice} />
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <div className="flex items-center gap-x-3">
            <button
              onClick={() => navigate("/organizations")}
              className="text-sm text-indigo-600 hover:text-indigo-900"
            >
              ← Back to Organizations
            </button>
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              {organization.type === 'vasi' ? 'Staff' : 'Users'} - {organization.name}
            </h1>
          </div>
          <p className="mt-2 text-sm text-gray-700">
            A list of all {organization.type === 'vasi' ? 'staff members' : 'users'} in this organization.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => setCreatingStaff(true)}
          >
            Add {organization.type === 'vasi' ? 'Staff Member' : 'User'}
          </button>
        </div>
      </div>
      {notice && (
        <div className="mt-4 rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{notice}</div>
        </div>
      )}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Role
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Activation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {staff.map((member) => (
                  <tr key={member.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                      {member.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {member.email}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {member.role}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                        member.active
                          ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-700/10'
                          : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-700/10'
                      }`}>
                        {member.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {!member.uid ? (
                        <span title="This profile has no linked login, so they can't sign in.">No login</span>
                      ) : activation[member.id] === "sent" ? (
                        "Email sent"
                      ) : (
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                          disabled={activation[member.id] === "sending"}
                          onClick={() => handleSendActivation(member)}
                        >
                          {activation[member.id] === "failed" ? "Failed - retry" : "Send activation email"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
